import { Rule } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { RESULTS_LIMIT_EXISTS } from "@/constants/resultsLimits";
import { reprocessedRuleIds } from "@/pages/zone-report/utils/reprocessCoverage";

/** Geotab reprocesses at most 6 months back; a stay older than that cannot come from a reprocessing. */
const REPROCESS_LOOKBACK_MS = 200 * 24 * 3_600_000;

/**
 * Ids of the zonelog rules whose history Geotab reprocessed: at most one 1-row read per creation batch when
 * it was reprocessed, one per rule when it was not.
 */
export function useZoneLogReprocessed(rules: Rule[]) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const ruleIds = rules.map(rule => rule.id).sort();

  return useQuery({
    queryKey: ["zoneLogReprocessed", activeSessionId, ruleIds],
    enabled: rules.length > 0,
    queryFn: ({ signal }) =>
      reprocessedRuleIds(rules, async rule => {
        const created = new Date(rule.activeFrom);
        const events = await geotabClient.call<unknown[]>(
          "Get",
          {
            typeName: "ExceptionEvent",
            search: {
              ruleSearch: { id: rule.id },
              fromDate: new Date(created.getTime() - REPROCESS_LOOKBACK_MS).toISOString(),
              toDate: created.toISOString(),
            },
            resultsLimit: RESULTS_LIMIT_EXISTS,
          },
          signal,
        );
        return events.length > 0;
      }),
  });
}
